// src/types/health/reportTypes.ts
// Report and Export Type Definitions for BrightPath Health Tracking

import { HealthLog, HealthLogType, Medication, HealthLogStats } from './healthTypes';

// ============================================================================
// Report Types
// ============================================================================

export type ReportType = 
  | 'comprehensive' 
  | 'seizure' 
  | 'medication' 
  | 'symptom' 
  | 'therapy' 
  | 'vitals' 
  | 'sleep' 
  | 'custom';

export type ReportPeriod = 'week' | 'month' | 'quarter' | 'year' | 'custom';

export type ReportFormat = 'pdf' | 'csv' | 'json' | 'excel';

export interface ReportSection {
  id: string;
  name: string;
  description: string;
  icon: string;
  enabled: boolean;
  order: number;
}

export interface ReportDateRange {
  startDate: Date;
  endDate: Date;
  period?: ReportPeriod;
}

export interface ReportConfiguration {
  id?: string;
  name: string;
  type: ReportType;
  childId: string;
  dateRange: ReportDateRange;
  sections: ReportSection[];
  includeCharts: boolean;
  includeRawData: boolean;
  includeSummary: boolean;
  includeInsights: boolean;
  customNotes?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  type: ReportType;
  icon: string;
  defaultSections: ReportSection[];
  suggestedPeriod: ReportPeriod;
  forProviders: boolean;
  forLegal: boolean;
}

// ============================================================================
// Generated Report Types
// ============================================================================

export interface ReportSummary {
  totalEntries: number;
  dateRange: ReportDateRange;
  mostCommonType: string;
  averageSeverity?: number;
  trends: ReportTrend[];
  highlights: string[];
  concerns: string[];
}

export interface ReportTrend {
  metric: string;
  direction: 'increasing' | 'decreasing' | 'stable';
  percentChange: number;
  description: string;
}

export interface ReportChartData {
  chartType: 'line' | 'bar' | 'pie' | 'area';
  title: string;
  data: Array<Record<string, unknown>>;
  xAxisKey: string;
  yAxisKeys: string[];
  colors: string[];
}

export interface GeneratedReport {
  id: string;
  configuration: ReportConfiguration;
  generatedAt: Date;
  childName: string;
  summary: ReportSummary;
  sections: GeneratedReportSection[];
  charts: ReportChartData[];
  rawData?: HealthLog[];
  insights?: ReportInsight[];
}

export interface GeneratedReportSection {
  id: string;
  name: string;
  content: ReportSectionContent;
}

export interface ReportSectionContent {
  type: 'table' | 'chart' | 'text' | 'stats' | 'timeline';
  data: unknown;
  description?: string;
}

export interface ReportInsight {
  id: string;
  type: 'positive' | 'negative' | 'neutral' | 'alert';
  title: string;
  description: string;
  metric?: string;
  value?: string | number;
  recommendation?: string;
}

// ============================================================================
// Saved Reports
// ============================================================================

export interface SavedReport {
  id: string;
  name: string;
  childId: string;
  childName: string;
  type: ReportType;
  dateRange: ReportDateRange;
  createdAt: Date;
  createdBy: string;
  fileUrl?: string;
  format: ReportFormat;
  size?: number;
  sharedWith?: string[];
}

// ============================================================================
// Export Types
// ============================================================================

export interface ExportConfiguration {
  format: ReportFormat;
  dateRange: ReportDateRange;
  childIds: string[];
  logTypes: string[];
  includeMedications: boolean;
  includeNotes: boolean;
  includeAttachments: boolean;
  groupBy: 'day' | 'week' | 'month' | 'type';
  sortBy: 'date' | 'type' | 'severity';
  sortOrder: 'asc' | 'desc';
}

export interface ExportResult {
  success: boolean;
  format: ReportFormat;
  filename: string;
  size: number;
  downloadUrl?: string;
  blob?: Blob;
  error?: string;
}

export interface ExportProgress {
  status: 'preparing' | 'generating' | 'compressing' | 'complete' | 'error';
  progress: number;
  message: string;
}

// ============================================================================
// Timeline Types
// ============================================================================

export interface TimelineEvent {
  id: string;
  type: 'health_log' | 'medication' | 'appointment' | 'milestone' | 'note';
  title: string;
  description?: string;
  timestamp: Date;
  category: string;
  severity?: number;
  icon: string;
  color: string;
  data?: HealthLog | Medication | unknown;
  relatedEvents?: string[];
}

export interface TimelineDay {
  date: Date;
  events: TimelineEvent[];
  summary?: {
    totalEvents: number;
    byType: Record<string, number>;
  };
}

export interface TimelineFilters {
  types: string[];
  categories: string[];
  severity?: {
    min: number;
    max: number;
  };
  searchQuery?: string;
}

export interface TimelineViewOptions {
  groupBy: 'day' | 'week' | 'month';
  showMedications: boolean;
  showAppointments: boolean;
  expandAll: boolean;
  highlightSevere: boolean;
}

// ============================================================================
// Dashboard Types
// ============================================================================

export interface HealthDashboardData {
  child: {
    id: string;
    name: string;
    avatarUrl?: string;
  };
  stats: HealthLogStats;
  recentLogs: HealthLog[];
  upcomingMedications: MedicationReminder[];
  alerts: HealthAlert[];
  insights: ReportInsight[];
  quickActions: QuickAction[];
}

export interface MedicationReminder {
  id: string;
  medicationId: string;
  medicationName: string;
  dosage: string;
  scheduledTime: Date;
  status: 'pending' | 'taken' | 'missed' | 'skipped';
}

export interface HealthAlert {
  id: string;
  type: 'warning' | 'info' | 'success' | 'urgent';
  title: string;
  message: string;
  timestamp: Date;
  actionUrl?: string;
  actionLabel?: string;
  dismissed: boolean;
}

export interface QuickAction {
  id: string;
  label: string;
  icon: string;
  action: string;
  color?: string;
  count?: number;
}

// ============================================================================
// Print Types
// ============================================================================

export interface PrintOptions {
  orientation: 'portrait' | 'landscape';
  paperSize: 'letter' | 'legal' | 'a4';
  margins: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  includeHeader: boolean;
  includeFooter: boolean;
  includePageNumbers: boolean;
  colorMode: 'color' | 'grayscale';
}

export interface PrintableReportData {
  title: string;
  subtitle?: string;
  childName: string;
  dateRange: string;
  generatedAt: Date;
  generatedBy: string;
  sections: PrintableSection[];
  footer?: string;
}

export interface PrintableSection {
  title: string;
  type: 'table' | 'chart' | 'text' | 'stats';
  content: unknown;
  pageBreakBefore?: boolean;
}

// ============================================================================
// API Response Types
// ============================================================================

export interface ReportListResponse {
  reports: SavedReport[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface GenerateReportRequest {
  configuration: ReportConfiguration;
  format?: ReportFormat;
  save?: boolean;
}

export interface GenerateReportResponse {
  report: GeneratedReport;
  savedReport?: SavedReport;
  downloadUrl?: string;
}

// ============================================================================
// Hook Return Types
// ============================================================================

export interface UseHealthReportsReturn {
  reports: SavedReport[];
  isLoading: boolean;
  error: Error | null;
  generateReport: (config: ReportConfiguration) => Promise<GeneratedReport>;
  downloadReport: (reportId: string, format: ReportFormat) => Promise<void>;
  deleteReport: (reportId: string) => Promise<void>;
  shareReport: (reportId: string, userIds: string[]) => Promise<void>;
}

export interface UseHealthExportReturn {
  exportData: (config: ExportConfiguration) => Promise<ExportResult>;
  progress: ExportProgress | null;
  isExporting: boolean;
  cancelExport: () => void;
}

export interface UseHealthTimelineReturn {
  events: TimelineEvent[];
  days: TimelineDay[];
  isLoading: boolean;
  error: Error | null;
  filters: TimelineFilters;
  setFilters: (filters: TimelineFilters) => void;
  viewOptions: TimelineViewOptions;
  setViewOptions: (options: TimelineViewOptions) => void;
  loadMore: () => void;
  hasMore: boolean;
}

// Missing exports needed by HealthReportGenerator
export type ReportTimeframe = 'week' | 'month' | 'quarter' | 'year' | 'custom';
export type ExportFormat = 'pdf' | 'csv' | 'json' | 'excel';

import { z } from 'zod';

export const reportFormSchema = z.object({
  reportType: z.string(),
  timeframe: z.string(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  sections: z.array(z.string()).optional(),
  format: z.string().optional(),
});

export type ReportFormData = z.infer<typeof reportFormSchema>;

export const getReportTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    comprehensive: 'Comprehensive Report',
    medication: 'Medication Report',
    seizure: 'Seizure Report',
    therapy: 'Therapy Progress Report',
    summary: 'Summary Report',
  };
  return labels[type] || type;
};

export const getTimeframeLabel = (timeframe: string): string => {
  const labels: Record<string, string> = {
    week: 'Last 7 Days',
    month: 'Last 30 Days',
    quarter: 'Last 3 Months',
    year: 'Last 12 Months',
    custom: 'Custom Range',
  };
  return labels[timeframe] || timeframe;
};

